import { useEffect, useState } from 'react';
import { api } from '../api';
import type { WebhookFailure } from '../api';
import {
  Breadcrumbs,
  Button,
  Empty,
  ErrorBanner,
  List,
  ListRow,
  PageHead,
  Pill,
  formatDate,
  useAction,
  useLoad,
} from '../ui';
import { failureTitle, failureWhere } from '../WebhookAlerts';

/**
 * Every webhook that reached a real transaction or merchant and could not be used, newest
 * first, and the button that clears the banner. Staff only; see docs/protocol.md,
 * "Webhook alerts".
 */
export function WebhookFailures() {
  const first = useLoad(() => api.webhookFailures.list(), []);

  // Older pages are appended below the first; marking seen starts over.
  const [older, setOlder] = useState<WebhookFailure[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  useEffect(() => {
    setOlder([]);
    setNextBefore(first.data?.nextBefore ?? null);
  }, [first.data]);

  const action = useAction();
  async function loadOlder() {
    if (!nextBefore) return;
    await action.run(async () => {
      const page = await api.webhookFailures.list(nextBefore);
      setOlder((previous) => [...previous, ...page.entries]);
      setNextBefore(page.nextBefore);
    });
  }

  // Up to the newest one on screen rather than "everything", so a failure that arrives
  // while this page is open is not marked seen before anyone has.
  const newest = first.data?.entries[0];
  async function markSeen() {
    if (!newest) return;
    await action.run(async () => {
      await api.webhookFailures.acknowledge(newest.id);
      first.reload();
    });
  }

  const entries = [...(first.data?.entries ?? []), ...older];

  return (
    <>
      <Breadcrumbs trail={[{ label: 'Activity', to: '/activity' }, { label: 'Webhook alerts' }]} />
      <PageHead
        title="Webhook alerts"
        hint="Webhooks from a payment provider that named a real transaction or merchant and still could not be used. While a till waits, polling covers for a lost one; a payment made after the till gave up reaches the counter by webhook alone. Where a transaction below is not Confirmed, look for it on the provider's dashboard."
        actions={
          newest && !newest.acknowledgedAt ? (
            <Button disabled={action.busy} onClick={markSeen} className="max-sm:w-full">
              Mark all as seen
            </Button>
          ) : undefined
        }
      />

      <ErrorBanner message={first.error ?? action.error} />

      {first.loading && entries.length === 0 ? (
        <Empty>Loading…</Empty>
      ) : entries.length === 0 ? (
        <Empty>No webhook has failed.</Empty>
      ) : (
        <List>
          {entries.map((failure) => (
            <ListRow
              key={failure.id}
              title={
                <>
                  <span className="font-medium">{failureTitle(failure.kind)}</span>
                  {!failure.acknowledgedAt && <Pill tone="warn">New</Pill>}
                </>
              }
              subtitle={
                <>
                  {/* On a phone the time goes here rather than beside the row, where it
                      squeezed a long reason into half the width. */}
                  <Time value={failure.receivedAt} className="block sm:hidden" />
                  <span className="block text-text [overflow-wrap:anywhere]">{failure.reason}</span>
                  <span className="block">{failureWhere(failure)}</span>
                  {failure.cloudTxnId && <Transaction failure={failure} />}
                  {failure.acknowledgedAt && (
                    <span className="block">
                      Seen by {failure.acknowledgedBy?.email ?? 'a login since removed'},{' '}
                      {formatDate(failure.acknowledgedAt)}
                    </span>
                  )}
                </>
              }
              actions={<Time value={failure.receivedAt} className="max-sm:hidden" />}
            />
          ))}
        </List>
      )}

      {nextBefore && (
        <Button variant="ghost" className="max-sm:w-full" disabled={action.busy} onClick={loadOlder}>
          {action.busy ? 'Loading…' : 'Load older'}
        </Button>
      )}
    </>
  );
}

function Time({ value, className }: { value: string; className: string }) {
  return (
    <time dateTime={value} className={`whitespace-nowrap text-[12.5px] text-muted ${className}`}>
      {formatDate(value)}
    </time>
  );
}

/**
 * Which payment, and whether it got through anyway. The cloud holds a transaction only for
 * a while, so an older failure may name one it can no longer say anything about.
 */
function Transaction({ failure }: { failure: WebhookFailure }) {
  const txn = failure.transaction;
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 [overflow-wrap:anywhere]">
      <span className="font-mono text-[12px]">{failure.cloudTxnId}</span>
      {txn ? (
        <>
          <span>{formatMoney(txn.amountLaari, txn.currency)}</span>
          <Pill tone={txn.state === 'CONFIRMED' ? 'on' : 'warn'}>{stateName(txn.state)}</Pill>
        </>
      ) : (
        <span>no longer held here</span>
      )}
    </span>
  );
}

/** Laari to "MVR 1,500.00" without floating point: money here is a whole-laari string. */
function formatMoney(laari: string, currency: string): string {
  const value = BigInt(laari);
  const sign = value < 0n ? '-' : '';
  const abs = value < 0n ? -value : value;
  const cents = (abs % 100n).toString().padStart(2, '0');
  return `${currency} ${sign}${(abs / 100n).toLocaleString()}.${cents}`;
}

function stateName(state: string): string {
  switch (state) {
    case 'CONFIRMED':
      return 'Confirmed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'FAILED':
      return 'Failed';
    case 'EXPIRED':
      return 'Expired';
    default:
      // CREATED and QR_GENERATED: as far as the cloud knows, nobody has paid.
      return 'Not confirmed';
  }
}
